import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin } from "lucide-react";

const FILTERS = ["All", "Phoenix", "Scottsdale", "Gilbert", "Virtual"];

export default function Events() {
  const [city, setCity] = useState("All");
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState([]);

  const load = async () => {
    const params = {};
    if (city !== "All") params.city = city;
    const [{ data: e }, { data: r }] = await Promise.all([
      api.get("/events", { params }),
      api.get("/events/my-rsvps"),
    ]);
    setEvents(e);
    setRsvps(r.map((x) => x.event_id));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city]);

  const onRsvp = async (event_id) => {
    try {
      await api.post("/events/rsvp", { event_id });
      setRsvps((r) => [...new Set([...r, event_id])]);
      toast.success("RSVP confirmed.");
    } catch {
      toast.error("Could not RSVP");
    }
  };

  return (
    <div data-testid="dashboard-events-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">Events</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">
        Meet the women.<br /><em>Move your business.</em>
      </h1>

      <div className="mt-10 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setCity(f)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-all ${city === f ? "bg-[var(--lh-black)] text-white border-[var(--lh-black)]" : "border-[var(--lh-border)] hover:border-[var(--lh-mauve)]"}`}
            data-testid={`events-filter-${f.toLowerCase()}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {events.map((e) => {
          const d = new Date(e.date);
          const day = d.getDate();
          const month = d.toLocaleString("en", { month: "short" }).toUpperCase();
          const already = rsvps.includes(e.event_id);
          return (
            <article key={e.event_id} className="editorial-card overflow-hidden flex flex-col" data-testid={`dashboard-event-${e.event_id}`}>
              {e.image_url && <div className="aspect-[16/9] overflow-hidden bg-[var(--lh-surface)]"><img src={e.image_url} alt={e.title} loading="lazy" decoding="async" className="w-full h-full object-cover" /></div>}
              <div className="p-7 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-serif-display text-5xl leading-none">{day}</div>
                    <div className="overline text-[var(--lh-mauve)] mt-1">{month}</div>
                  </div>
                  <span className="overline text-[var(--lh-muted)] border border-[var(--lh-border)] px-2 py-1">{e.city}</span>
                </div>
                <h3 className="font-serif-display text-2xl leading-tight">{e.title}</h3>
                <div className="overline text-[var(--lh-muted)] mt-2 flex items-center gap-1.5"><Calendar size={11} /> {e.time}</div>
                <div className="overline text-[var(--lh-muted)] mt-1 flex items-center gap-1.5"><MapPin size={11} /> {e.venue}</div>
                <p className="text-sm font-light text-[var(--lh-text)] mt-3 flex-1">{e.description}</p>
                <button
                  onClick={() => onRsvp(e.event_id)}
                  disabled={already}
                  className={already ? "btn-ghost mt-5 justify-center" : "btn-primary mt-5 justify-center"}
                  data-testid={`event-rsvp-${e.event_id}`}
                >
                  {already ? "RSVP'd ✓" : "RSVP"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
